import { useCallback, useEffect, useRef, useState } from 'react'
import type { RefObject } from 'react'
import { DEFAULT_GRID_GEOMETRY, topToFestivalMinutes } from './timeUtils'
import type { GridGeometry } from './timeUtils'

/**
 * Dragging a horizontal line on the schedule grid to a new time: pointer →
 * grid-minute math, clamped to the grid and optionally snapped, with a live
 * `dragMin` preview and a commit on release. Listeners go on the window so the
 * drag keeps tracking outside the handle.
 *
 * With `threshold`, the gesture only becomes a drag once the pointer moves that
 * many px; releasing before then is a tap (`onTap`), so one handle can do both.
 */
export function useLineDrag({
  gridRef,
  gridMin,
  gridMax,
  geometry = DEFAULT_GRID_GEOMETRY,
  snap,
  threshold = 0,
  onCommit,
  onTap,
}: {
  gridRef: RefObject<HTMLDivElement | null>
  gridMin: number
  gridMax: number
  geometry?: GridGeometry
  /** Round the dragged time to this many minutes. */
  snap?: number
  threshold?: number
  onCommit: (minutes: number) => void
  onTap?: () => void
}): { dragMin: number | null; startDrag: (e: React.PointerEvent) => void } {
  const [dragMin, setDragMin] = useState<number | null>(null)
  const teardownRef = useRef<(() => void) | null>(null)

  // If we unmount mid-drag, drop the window listeners.
  useEffect(() => () => teardownRef.current?.(), [])

  const startDrag = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault()
      const startY = e.clientY
      let dragging = threshold === 0
      let last: number | null = null
      const toMinutes = (clientY: number) => {
        const grid = gridRef.current
        if (!grid) return gridMin
        const raw = topToFestivalMinutes(clientY - grid.getBoundingClientRect().top, gridMin, geometry)
        const snapped = snap ? Math.round(raw / snap) * snap : raw
        return Math.max(gridMin, Math.min(gridMax, snapped))
      }
      const move = (ev: PointerEvent) => {
        if (!dragging && Math.abs(ev.clientY - startY) < threshold) return
        dragging = true
        last = toMinutes(ev.clientY)
        setDragMin(last)
      }
      // preventDefault on pointerdown doesn't stop a mouse drag from selecting
      // the text it passes over (stage headers, set names), so block it outright.
      const userSelect = document.body.style.userSelect
      document.body.style.userSelect = 'none'
      const teardown = () => {
        document.body.style.userSelect = userSelect
        window.removeEventListener('pointermove', move)
        window.removeEventListener('pointerup', end)
        window.removeEventListener('pointercancel', cancel)
        teardownRef.current = null
        setDragMin(null)
      }
      const end = () => {
        teardown()
        if (!dragging) onTap?.()
        else if (last != null) onCommit(last)
      }
      const cancel = () => teardown()
      teardownRef.current = teardown
      window.addEventListener('pointermove', move)
      window.addEventListener('pointerup', end)
      window.addEventListener('pointercancel', cancel)
      if (dragging) move(e.nativeEvent)
    },
    [gridRef, gridMin, gridMax, geometry, snap, threshold, onCommit, onTap],
  )

  return { dragMin, startDrag }
}
